'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';

const Header: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [activeTab, setActiveTab] = useState('/');

  useEffect(() => {
    const handleScroll = () => {
      setIsSticky(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);

    if (window.location.hash) {
      setActiveTab('/' + window.location.hash);
    }

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const menuItems = [
    { label: 'Home', href: '/' },
    { label: 'Events', href: '/#events' },
    { label: 'Speakers', href: '/#speakers' },
    { label: 'Partners', href: '/#partners' },
    { label: 'About', href: '/#about' },
    { label: 'Community & Membership', href: '/#CommunityMembership' },
  ];

  const handleSmoothScroll = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    const [path, hash] = href.split('#');

    setActiveTab(href);

    if (hash && window.location.pathname === path) {
      e.preventDefault();

      const el = document.getElementById(hash);

      if (el) {
        el.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }
    }

    setIsMobileMenuOpen(false);
  };

  const getLinkClasses = (href: string) => {
    return activeTab === href
      ? 'px-4 py-2 rounded-2xl bg-[#FF00B6] text-white text-sm md:text-base font-[600] shadow-md transition'
      : 'text-sm md:text-base font-[600] text-black hover:text-[#FF00B6] transition-colors duration-200';
  };

  return (
    <header data-component-id="src/components/common/Header.tsx:65:4" data-component-path="src/components/common/Header.tsx" data-component-line="65" data-component-end-line="175" data-component-file="Header.tsx" data-component-name="header" data-component-content="%7B%22elementName%22%3A%22header%22%7D"
      className={`w-full z-50 bg-white transition-all duration-300 ${
        isSticky
          ? 'fixed top-0 left-0 shadow-lg'
          : 'absolute top-0 left-0'
      }`}
    >
      <div data-component-id="src/components/common/Header.tsx:72:6" data-component-path="src/components/common/Header.tsx" data-component-line="72" data-component-end-line="141" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20max-w-%5B1440px%5D%20mx-auto%20px-4%20sm%3Apx-6%20lg%3Apx-%5B76px%5D%22%7D" className="w-full max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-[76px]">
        <div data-component-id="src/components/common/Header.tsx:73:8" data-component-path="src/components/common/Header.tsx" data-component-line="73" data-component-end-line="140" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20justify-between%20items-center%20py-3%20sm%3Apy-4%20lg%3Apy-5%22%7D" className="flex justify-between items-center py-3 sm:py-4 lg:py-5">
          
          {/* Logo */}
          <Link data-component-id="src/components/common/Header.tsx:76:10" data-component-path="src/components/common/Header.tsx" data-component-line="76" data-component-end-line="85" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2F%22%2C%22className%22%3A%22flex%20items-center%22%7D" href="/" className="flex items-center">
            <Image data-component-id="src/components/common/Header.tsx:77:12" data-component-path="src/components/common/Header.tsx" data-component-line="77" data-component-end-line="84" data-component-file="Header.tsx" data-component-name="Image" data-component-content="%7B%22elementName%22%3A%22Image%22%2C%22src%22%3A%22%2Fimages%2Fmain-logo.svg%22%2C%22alt%22%3A%22logo%22%2C%22className%22%3A%22h-auto%20w-%5B120px%5D%20sm%3Aw-%5B130px%5D%20lg%3Aw-%5B140px%5D%22%7D"
              src="/images/main-logo.svg"
              alt="logo"
              width={140}
              height={40}
              priority
              className="h-auto w-[120px] sm:w-[130px] lg:w-[140px]"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav data-component-id="src/components/common/Header.tsx:88:10" data-component-path="src/components/common/Header.tsx" data-component-line="88" data-component-end-line="99" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22hidden%20lg%3Aflex%20items-center%20gap-6%22%7D" className="hidden lg:flex items-center gap-6">
            {menuItems.map((item, index) => (
              <Link data-component-id="src/components/common/Header.tsx:90:14" data-component-path="src/components/common/Header.tsx" data-component-line="90" data-component-end-line="97" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                key={index}
                href={item.href}
                onClick={(e) => handleSmoothScroll(e, item.href)}
                className={getLinkClasses(item.href)}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Button */}
          <Link data-component-id="src/components/common/Header.tsx:102:10" data-component-path="src/components/common/Header.tsx" data-component-line="102" data-component-end-line="106" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fcontact%2F%22%2C%22className%22%3A%22hidden%20lg%3Ablock%22%7D" href="/contact/" className="hidden lg:block">
            <button data-component-id="src/components/common/Header.tsx:103:12" data-component-path="src/components/common/Header.tsx" data-component-line="103" data-component-end-line="105" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22px-5%20py-2%20text-white%20text-sm%20sm%3Atext-base%20font-%5B600%5D%20shadow-md%20rounded-2xl%20bg-%5B%23FF00B6%5D%20hover%3Aopacity-90%20transition%22%2C%22textContent%22%3A%22Contact%22%7D" className="px-5 py-2 text-white text-sm sm:text-base font-[600] shadow-md rounded-2xl bg-[#FF00B6] hover:opacity-90 transition">
              Contact
            </button>
          </Link>

          {/* Mobile Hamburger */}
          <button data-component-id="src/components/common/Header.tsx:109:10" data-component-path="src/components/common/Header.tsx" data-component-line="109" data-component-end-line="139" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22lg%3Ahidden%20p-3%22%7D"
            className="lg:hidden p-3"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            <div data-component-id="src/components/common/Header.tsx:113:12" data-component-path="src/components/common/Header.tsx" data-component-line="113" data-component-end-line="138" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-6%20h-6%20flex%20flex-col%20justify-center%20items-center%22%7D" className="w-6 h-6 flex flex-col justify-center items-center">
              
              <span data-component-id="src/components/common/Header.tsx:115:14" data-component-path="src/components/common/Header.tsx" data-component-line="115" data-component-end-line="121" data-component-file="Header.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                className={`block w-6 h-0.5 bg-black transition-all duration-300 ${
                  isMobileMenuOpen
                    ? 'rotate-45 translate-y-1.5'
                    : ''
                }`}
              />

              <span data-component-id="src/components/common/Header.tsx:123:14" data-component-path="src/components/common/Header.tsx" data-component-line="123" data-component-end-line="129" data-component-file="Header.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                className={`block w-6 h-0.5 bg-black mt-1 transition-all duration-300 ${
                  isMobileMenuOpen
                    ? 'opacity-0'
                    : ''
                }`}
              />

              <span data-component-id="src/components/common/Header.tsx:131:14" data-component-path="src/components/common/Header.tsx" data-component-line="131" data-component-end-line="137" data-component-file="Header.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                className={`block w-6 h-0.5 bg-black mt-1 transition-all duration-300 ${
                  isMobileMenuOpen
                    ? '-rotate-45 -translate-y-1.5'
                    : ''
                }`}
              />
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <nav data-component-id="src/components/common/Header.tsx:145:8" data-component-path="src/components/common/Header.tsx" data-component-line="145" data-component-end-line="173" data-component-file="Header.tsx" data-component-name="nav" data-component-content="%7B%22elementName%22%3A%22nav%22%2C%22className%22%3A%22lg%3Ahidden%20absolute%20top-full%20left-0%20w-full%20bg-white%20z-50%20border-t%20shadow-md%22%7D" className="lg:hidden absolute top-full left-0 w-full bg-white z-50 border-t shadow-md">
          <div data-component-id="src/components/common/Header.tsx:146:10" data-component-path="src/components/common/Header.tsx" data-component-line="146" data-component-end-line="172" data-component-file="Header.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-col%20p-4%20space-y-3%22%7D" className="flex flex-col p-4 space-y-3">
            
            {menuItems.map((item, index) => (
              <Link data-component-id="src/components/common/Header.tsx:149:14" data-component-path="src/components/common/Header.tsx" data-component-line="149" data-component-end-line="160" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%7D"
                key={index}
                href={item.href}
                onClick={(e) => handleSmoothScroll(e, item.href)}
                className={`w-full ${
                  activeTab === item.href
                    ? 'px-4 py-2 rounded-2xl bg-[#FF00B6] text-white font-[600]'
                    : 'text-sm text-black hover:text-[#FF00B6] py-2 font-[600]'
                }`}
              >
                {item.label}
              </Link>
            ))}

            <Link data-component-id="src/components/common/Header.tsx:163:12" data-component-path="src/components/common/Header.tsx" data-component-line="163" data-component-end-line="170" data-component-file="Header.tsx" data-component-name="Link" data-component-content="%7B%22elementName%22%3A%22Link%22%2C%22href%22%3A%22%2Fcontact%2F%22%7D"
              href="/contact/"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <button data-component-id="src/components/common/Header.tsx:167:14" data-component-path="src/components/common/Header.tsx" data-component-line="167" data-component-end-line="169" data-component-file="Header.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20px-5%20py-2%20text-white%20text-sm%20font-%5B600%5D%20rounded-2xl%20bg-%5B%23FF00B6%5D%22%2C%22textContent%22%3A%22Contact%22%7D" className="w-full px-5 py-2 text-white text-sm font-[600] rounded-2xl bg-[#FF00B6]">
                Contact
              </button>
            </Link>

          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;